import { Injectable } from '@angular/core';
import { Chats, Preferences, Workspace } from '@local/client-contracts';
import { observable } from '@local/common';
import { isExtension } from '@local/common-web';
import { INIT_POSITION_POPUP, PopupRef, PopupService } from '@local/ui-infra';
import { ContextMenuAction, EmbedService } from '@shared/embed.service';
import { LogService } from '@shared/services';
import { PreferencesService } from '@shared/services/preferences.service';
import { Logger } from '@unleash-tech/js-logger';
import { capitalize } from 'lodash';
import {
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  of,
  ReplaySubject,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { WorkspacesService } from 'src/app/bar/services';
import { MessagePopupComponent, MessagePopupModel, MessagePopupActionModel } from '../components/message-popup/message-popup.component';
import { AssistantDetails, ChatsService } from '../views/chat-page/services/chats.service';
import { ChatAssistantsService } from './chat-assistants.service';

@Injectable()
export class WritingAssistantService {
  private readonly isExtension = isExtension();
  private readonly ADMIN_ERROR_ACTION: MessagePopupActionModel = {
    text: 'Select Assistant',
    url: '/admin/extension/setup',
    icon: 'arrow-right-window',
  };
  private readonly ADMIN_ERROR: MessagePopupModel = {
    title: 'Action is not available',
    description:
      'Writing tools are unavailable as the selected assistant has been deleted or unpinned from Chat. Please select a new one to proceed.',
    action: this.ADMIN_ERROR_ACTION,
  };
  private readonly ADMIN_ERROR_NOT_SELECTED_ASSISTANT: MessagePopupModel = {
    title: 'Set Up Writing Tools',
    description: 'Enable writing tools in the browser extension by selecting an assistant for your workspace.',
    action: this.ADMIN_ERROR_ACTION,
  };
  private readonly MEMBER_ERROR: MessagePopupModel = {
    title: 'Action is not available',
    description: 'Please contact your Workspace Administrator.',
  };
  static DEFAULT_LANGUAGE = 'english';

  private logger: Logger;
  private _userMessage$ = new Subject<Chats.UserMessage>();
  private _userPreferences: Preferences.Preferences;
  private _workspace: Workspace.Workspace;
  private _defaultAssistant$ = new ReplaySubject<AssistantDetails>(1);
  private errorPopupRef: PopupRef<MessagePopupComponent, MessagePopupModel>;

  private set defaultAssistant(value: AssistantDetails) {
    this._defaultAssistant$.next(value);
  }

  get userPreferences(): Preferences.Preferences {
    return this._userPreferences;
  }

  get workspace(): Workspace.Workspace {
    return this._workspace;
  }

  @observable
  get defaultAssistant$(): Observable<AssistantDetails> {
    return this._defaultAssistant$.asObservable();
  }

  @observable
  get userMessage$(): Subject<Chats.UserMessage> {
    return this._userMessage$;
  }

  @observable
  get defaultWorkspaceLanguage$(): Observable<string> {
    return this.workspacesService.current$.pipe(
      map((workspace) => workspace?.settings?.assistant?.writingAssistant?.defaultLanguage || WritingAssistantService.DEFAULT_LANGUAGE)
    );
  }

  @observable
  get defaultUserLanguage$(): Observable<string> {
    return this.preferencesService.current$.pipe(
      switchMap((userPreferences) =>
        userPreferences.general.defaultLanguage ? of(userPreferences.general.defaultLanguage) : this.defaultWorkspaceLanguage$
      )
    );
  }

  constructor(
    logger: LogService,
    private embedService: EmbedService,
    private preferencesService: PreferencesService,
    private workspacesService: WorkspacesService,
    private chatsService: ChatsService,
    private chatAssistantsService: ChatAssistantsService,
    private popupService: PopupService
  ) {
    this.logger = logger.scope('WritingAssistantService');
    this.initSubscription();
  }

  private initSubscription() {
    if (this.isExtension) {
      this.embedService.onContextMenuItemClick$.subscribe(async (item: ContextMenuAction) => {
        await this.sendMessage(item);
      });
    }
    this.preferencesService.current$.subscribe((preferences) => {
      this._userPreferences = preferences;
    });
    combineLatest([
      this.workspacesService.current$.pipe(distinctUntilChanged()),
      this.chatAssistantsService.assistantForChat$.pipe(distinctUntilChanged(), startWith(null)),
    ]).subscribe(([workspace, _]) => {
      if (workspace) {
        this._workspace = workspace;
      }
      this.setValidAssistant();
    });
  }

  private async setValidAssistant() {
    const assistantId = this._workspace?.settings?.assistant?.writingAssistant?.experienceId;
    if (!assistantId) {
      this.defaultAssistant = { assistant: null, error: null };
      return;
    }
    this.defaultAssistant = await this.chatsService.getValidAssistant(assistantId, 'first');
  }

  private async sendMessage(item: ContextMenuAction) {
    if (this.errorPopupRef) {
      this.errorPopupRef.destroy();
    }

    const defaultAssistant = await firstValueFrom(this._defaultAssistant$);

    if (defaultAssistant?.error || !defaultAssistant?.assistant) {
      return this.openErrorPopup(defaultAssistant);
    }

    const assistantId = defaultAssistant.assistant.id;
    const currentChat = await firstValueFrom(this.chatsService.currentChat$);

    const userMessage = await this.getUserMessage(item);

    if (currentChat?.assistant?.id !== assistantId) {
      return this.chatsService.openChat(assistantId, null, null, null, { userMessage });
    }

    this._userMessage$.next(userMessage);
  }

  async getUserMessage(item: ContextMenuAction): Promise<Chats.UserMessage> {
    const scope: Chats.UserMessageActionScope = item.selectionText?.length ? 'quote' : 'site';
    let action: Chats.UserMessageAction = scope === 'quote' ? this.getQuoteMessage(item) : this.getSiteMessage(item);
    if (item.menuItemId.startsWith('translate')) {
      action = await this.getTranslateMessage(item, action);
    }
    return {
      action,
      timestamp: Date.now(),
    };
  }

  private getQuoteMessage(item: ContextMenuAction): Chats.QuoteUserMessageAction {
    return {
      scope: 'quote',
      type: item.menuItemId as Chats.UserMessageActionType,
      quote: item.selectionText,
    };
  }

  private getSiteMessage(item: ContextMenuAction): Chats.SiteUserMessageAction {
    return {
      scope: 'site',
      type: item.menuItemId as Chats.UserMessageActionType,
      siteUrl: item.frameUrl,
    };
  }

  private async getTranslateMessage(item: ContextMenuAction, action: Chats.UserMessageAction): Promise<Chats.UserMessageAction> {
    const updatedAction: Chats.TranslateMessage = {
      ...action,
      type: 'translate',
      language: await firstValueFrom(this.defaultUserLanguage$),
    };
    if (item.menuItemId.startsWith('translate-to')) {
      const language = item.menuItemId.split('-').pop();
      updatedAction.language = language;
    }
    return updatedAction;
  }

  updateUserLanguage(language: string) {
    if (!this.userPreferences) {
      this.logger.error('missing preferences for save user language');
      return;
    }
    this.userPreferences.general.defaultLanguage = language.toLowerCase();
    this.preferencesService.update(this.userPreferences);
  }

  updateWritingAssistant(language?: string, experienceId?: string, changeAssistant?: boolean) {
    if (!this.workspace?.settings?.assistant) {
      this.logger.error('missing workspace for save writing assistant');
      return;
    }

    const lang = language || this.workspace.settings.assistant.writingAssistant?.defaultLanguage;
    const exId = changeAssistant ? experienceId : this.workspace.settings.assistant.writingAssistant?.experienceId;

    this.workspace.settings.assistant.writingAssistant = {
      defaultLanguage: lang?.toLowerCase(),
      experienceId: exId,
    };

    this.workspacesService.update(this.workspace);
  }

  getViewAction(actionType: string, language?: string) {
    switch (actionType) {
      case 'improve': {
        return {
          text: 'Improve writing',
          icon: 'icon-improve',
        };
      }
      case 'summarize': {
        return {
          text: 'Summarize',
          icon: 'icon-board-list',
        };
      }
      default: {
        return {
          text: `Translate into ${capitalize(language)}`,
          icon: 'icon-language',
        };
      }
    }
  }

  private async openErrorPopup(defaultAssistant: AssistantDetails) {
    const ownerOrAdmin = this.workspace.isOwner || this.workspace.isAdmin;
    let data;
    if (ownerOrAdmin) {
      data = defaultAssistant.error ? this.ADMIN_ERROR : this.ADMIN_ERROR_NOT_SELECTED_ASSISTANT;
    } else {
      data = this.MEMBER_ERROR;
    }

    this.errorPopupRef = this.popupService.open<MessagePopupComponent, MessagePopupModel>({ x: 0, y: 0 }, MessagePopupComponent, data, {
      position: INIT_POSITION_POPUP,
      panelClass: 'writing-assistant-message-popup',
    });
  }
}
